import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useAiChatDatasourceRows from '../hooks/useAiChatDatasourceRows';
import buildPieChartData from '../utils/buildPieChartData';
import AiChatChartFrame from './AiChatChartFrame';
import AiChatPieChart from './AiChatPieChart';

import type { ReactElement } from 'react';
import type IAiChatPieChartDisplay from '../interfaces/IAiChatPieChartDisplay';

/** A `showChart` call of type `pie`, done: every row of the datasource read, then drawn as slices. */
function AiChatPieChartDisplay({ shown }: IAiChatPieChartDisplay): ReactElement {
  const { t } = useTranslation();
  const {
    columns, rows, executedAt, droppedReason, isLoading, isError,
  } = useAiChatDatasourceRows(shown.datasourceId, shown.rowCount);

  const otherLabel = t('aiChat.tools.chartOther');
  const data = useMemo(
    () => buildPieChartData(columns, rows, shown, otherLabel),
    [columns, rows, shown, otherLabel],
  );

  const tableColumns = useMemo(() => [shown.labelColumn, shown.valueColumn], [shown]);

  const note = data.foldedSlices
    ? t('aiChat.tools.chartSlicesFolded', { count: data.foldedSlices, other: otherLabel })
    : undefined;

  return (
    <AiChatChartFrame
      datasourceId={shown.datasourceId}
      title={shown.title}
      columns={tableColumns}
      rowCount={shown.rowCount}
      truncated={shown.truncated}
      isLoading={isLoading}
      isError={isError}
      executedAt={executedAt}
      droppedReason={droppedReason}
      note={note}
    >
      {data.slices.length ? <AiChatPieChart data={data} /> : <p>{t('aiChat.tools.chartEmpty')}</p>}
    </AiChatChartFrame>
  );
}

export default AiChatPieChartDisplay;
